import React, { useState, useEffect, useMemo, useCallback } from 'react';
import {
  Search,
  Pencil,
  Power,
  X,
  Link2,
  Loader2,
  Users,
  Briefcase,
  Phone,
  RefreshCw,
  ChevronLeft,
  ChevronRight,
  MapPin,
  Smartphone,
  ShieldCheck,
  ShieldAlert,
  Wallet,
  Check,
  Ticket,
  GitMerge,
} from 'lucide-react';
import { SUPABASE_URL, SUPABASE_ANON_KEY } from '../../config/env.js';
import { serviceRoleClient } from '../../services/serviceRoleClient';
import PartnerCoverageMap from '../../components/admin/PartnerCoverageMap';
import MechanicAppPreviewModal from '../../components/admin/MechanicAppPreviewModal';

const PAGE_SIZE_OPTIONS = [25, 50, 100];

const TYPE_LABEL = {
  mechanic: 'Mechanic',
  towing: 'Towing',
  both: 'Mech + Towing',
};

const TYPE_BADGE = {
  mechanic: 'bg-sky-50 text-sky-800 ring-sky-600/15',
  towing: 'bg-violet-50 text-violet-800 ring-violet-600/15',
  both: 'bg-indigo-50 text-indigo-800 ring-indigo-600/15',
};

const authHeaders = {
  apikey: SUPABASE_ANON_KEY,
  Authorization: `Bearer ${SUPABASE_ANON_KEY}`,
};

const formatValue = (value) => {
  if (value === null || value === undefined || value === '') return '—';
  if (typeof value === 'boolean') return value ? 'Yes' : 'No';
  if (typeof value === 'object') {
    try {
      return JSON.stringify(value, null, 2);
    } catch {
      return String(value);
    }
  }
  return String(value);
};

const formatWhen = (iso) => {
  if (!iso) return '—';
  try {
    return new Date(iso).toLocaleString('en-IN', {
      day: '2-digit',
      month: 'short',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  } catch {
    return '—';
  }
};

const HOLD_STATUS = {
  consumed: { label: 'Used', className: 'bg-emerald-50 text-emerald-700' },
  reserved: { label: 'On job', className: 'bg-amber-50 text-amber-700' },
  released: { label: 'Released', className: 'bg-slate-100 text-slate-600' },
};

const inputClass =
  'mt-0.5 w-full rounded-md border-0 bg-transparent p-0 text-sm font-medium text-slate-900 outline-none placeholder:text-slate-400 focus:ring-0';

const EditableField = ({
  label,
  value,
  onChange,
  type = 'text',
  mono = false,
  disabled = false,
  options = null,
  placeholder = '',
}) => (
  <label className={`rounded-lg border px-3 py-2 ${disabled ? 'border-slate-100 bg-slate-50/70' : 'border-indigo-100 bg-white shadow-sm'}`}>
    <p className="text-[10px] font-semibold uppercase tracking-wide text-slate-500">{label}</p>
    {disabled ? (
      <p className={`mt-0.5 whitespace-pre-wrap break-words text-sm text-slate-900 ${mono ? 'font-mono text-xs' : 'font-medium'}`}>
        {formatValue(value)}
      </p>
    ) : options ? (
      <select
        value={value ?? ''}
        onChange={(e) => onChange(e.target.value)}
        className={`${inputClass} cursor-pointer`}
      >
        {options.map((opt) => (
          <option key={opt.value} value={opt.value}>
            {opt.label}
          </option>
        ))}
      </select>
    ) : type === 'textarea' ? (
      <textarea
        rows={3}
        value={value ?? ''}
        placeholder={placeholder || '—'}
        onChange={(e) => onChange(e.target.value)}
        className={`${inputClass} resize-y ${mono ? 'font-mono text-xs' : ''}`}
      />
    ) : type === 'checkbox' ? (
      <select
        value={value ? 'true' : 'false'}
        onChange={(e) => onChange(e.target.value === 'true')}
        className={`${inputClass} cursor-pointer`}
      >
        <option value="true">Yes</option>
        <option value="false">No</option>
      </select>
    ) : (
      <input
        type={type}
        value={value ?? ''}
        placeholder={placeholder || '—'}
        onChange={(e) => onChange(e.target.value)}
        className={`${inputClass} ${mono ? 'font-mono text-xs' : ''}`}
      />
    )}
  </label>
);

const numOrNull = (v) => {
  if (v === '' || v == null) return null;
  const n = Number(v);
  return Number.isFinite(n) ? n : null;
};

const seedDetailForm = (m, owner = null) => {
  if (!m) return null;
  return {
    mechanic_name: m.mechanic_name || '',
    shop_name: m.shop_name || '',
    code: m.code || '',
    number: m.number || '',
    alternate_number1: m.alternate_number1 || '',
    alternate_number2: m.alternate_number2 || '',
    timing: m.timing || '',
    partner_type: m.partner_type || 'mechanic',
    city: m.city || '',
    area: m.area || '',
    state: m.state || '',
    district: m.district || '',
    service_tag: m.service_tag || '',
    service_tag2: m.service_tag2 || '',
    service_tag3: m.service_tag3 || '',
    experience_years: m.experience_years ?? '',
    service_radius_km: m.service_radius_km ?? '',
    service_center_lat: m.service_center_lat ?? '',
    service_center_lng: m.service_center_lng ?? '',
    owner_user_id: m.owner_user_id || '',
    public_slug: m.public_slug || '',
    is_public: !!m.is_public,
    kyc_status: m.kyc_status || '',
    kyc_verified_at: m.kyc_verified_at || '',
    kyc_rejection_reason: m.kyc_rejection_reason || '',
    logo_url: m.logo_url || '',
    bio: m.bio || '',
    owner_name: owner?.name || '',
    owner_email: owner?.email || '',
    owner_phone: owner?.phone || '',
    owner_status: owner?.status || '',
    roadserve_commission_percent: m.roadserve_commission_percent ?? 20,
    active: !!m.active,
    previous_assigned_cases: m.previous_assigned_cases ?? 0,
    city_slug: m.city_slug || '',
    area_slug: m.area_slug || '',
    auto_online_by_slots: !!m.auto_online_by_slots,
  };
};

const parseContentRangeTotal = (header) => {
  // e.g. "0-49/1763" or "*/1763"
  if (!header) return 0;
  const m = String(header).match(/\/(\d+|\*)$/);
  if (!m || m[1] === '*') return 0;
  return Number(m[1]) || 0;
};

const MechanicManagement = () => {
  const [mechanics, setMechanics] = useState([]);
  const [crewByPartner, setCrewByPartner] = useState({});
  const [ticketsByPartner, setTicketsByPartner] = useState({});
  const [detailTickets, setDetailTickets] = useState({ available: 0, reserved: 0 });
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [searchInput, setSearchInput] = useState('');
  const [filterActive, setFilterActive] = useState('all');
  const [filterLinked, setFilterLinked] = useState('all');
  const [filterKyc, setFilterKyc] = useState('all');
  const [selectedIds, setSelectedIds] = useState([]);
  const [bulkSaving, setBulkSaving] = useState(false);
  const [kycBusyId, setKycBusyId] = useState(null);
  const [sortBy, setSortBy] = useState('previous'); // previous | newest | name
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(50);
  const [totalCount, setTotalCount] = useState(0);
  const [stats, setStats] = useState({
    total: 0,
    active: 0,
    linked: 0,
    withPrev: 0,
    prevSum: 0,
    withTeam: 0,
    teamMembers: 0,
  });
  const [expandedTeamId, setExpandedTeamId] = useState(null);
  const [detailId, setDetailId] = useState(null);
  const [detail, setDetail] = useState(null);
  const [detailOwner, setDetailOwner] = useState(null);
  const [detailCrew, setDetailCrew] = useState([]);
  const [detailLoading, setDetailLoading] = useState(false);
  const [detailForm, setDetailForm] = useState(null);
  const [detailDirty, setDetailDirty] = useState(false);
  const [detailSaving, setDetailSaving] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [editName, setEditName] = useState('');
  const [editType, setEditType] = useState('mechanic');
  const [editPercent, setEditPercent] = useState('20');
  const [editOwner, setEditOwner] = useState('');
  const [saving, setSaving] = useState(false);
  const [coverageOpen, setCoverageOpen] = useState(false);
  const [previewPartner, setPreviewPartner] = useState(null);
  const [creditPartner, setCreditPartner] = useState(null);
  const [creditAmount, setCreditAmount] = useState('');
  const [creditRepayable, setCreditRepayable] = useState(false);
  const [creditNote, setCreditNote] = useState('');
  const [creditSaving, setCreditSaving] = useState(false);
  const [creditRows, setCreditRows] = useState([]);
  const [creditBalance, setCreditBalance] = useState(0);
  const [creditLoading, setCreditLoading] = useState(false);
  const [ticketHistoryPartner, setTicketHistoryPartner] = useState(null);
  const [ticketHistoryLoading, setTicketHistoryLoading] = useState(false);
  const [ticketHistoryTab, setTicketHistoryTab] = useState('purchases');
  const [ticketHistoryPurchases, setTicketHistoryPurchases] = useState([]);
  const [ticketHistoryHolds, setTicketHistoryHolds] = useState([]);
  const [ticketHistoryBalance, setTicketHistoryBalance] = useState(null);

  const totalPages = Math.max(1, Math.ceil(totalCount / pageSize));

  const countQuery = async (query = '') => {
    const qs = ['select=id', 'limit=1', query].filter(Boolean).join('&');
    const response = await fetch(`${SUPABASE_URL}/rest/v1/mechanics?${qs}`, {
      headers: { ...authHeaders, Prefer: 'count=exact' },
    });
    return parseContentRangeTotal(response.headers.get('content-range'));
  };

  const loadStats = useCallback(async () => {
    try {
      const [total, active, linked, withPrev] = await Promise.all([
        countQuery('merged_into_id=is.null'),
        countQuery('active=eq.true&merged_into_id=is.null'),
        countQuery('owner_user_id=not.is.null&merged_into_id=is.null'),
        countQuery('previous_assigned_cases=gt.0&merged_into_id=is.null'),
      ]);

      // Sum previous_assigned_cases across all partners (paginated)
      let prevSum = 0;
      let offset = 0;
      while (true) {
        const res = await fetch(
          `${SUPABASE_URL}/rest/v1/mechanics?select=previous_assigned_cases&merged_into_id=is.null&offset=${offset}&limit=1000`,
          { headers: authHeaders },
        );
        const rows = await res.json();
        if (!Array.isArray(rows) || rows.length === 0) break;
        prevSum += rows.reduce((s, r) => s + Number(r.previous_assigned_cases || 0), 0);
        if (rows.length < 1000) break;
        offset += 1000;
      }

      // Team stats
      let teamMembers = 0;
      let withTeam = 0;
      try {
        const crewCountRes = await fetch(
          `${SUPABASE_URL}/rest/v1/partner_crew_members?select=id&limit=1`,
          { headers: { ...authHeaders, Prefer: 'count=exact' } },
        );
        teamMembers = parseContentRangeTotal(crewCountRes.headers.get('content-range'));

        const partnersWithCrewRes = await fetch(
          `${SUPABASE_URL}/rest/v1/partner_crew_members?select=partner_id&limit=5000`,
          { headers: authHeaders },
        );
        const crewRows = await partnersWithCrewRes.json();
        if (Array.isArray(crewRows)) {
          withTeam = new Set(crewRows.map((r) => r.partner_id).filter(Boolean)).size;
        }
      } catch {
        // crew table may be empty / RLS
      }

      setStats({ total, active, linked, withPrev, prevSum, withTeam, teamMembers });
    } catch (e) {
      console.error('Error loading partner stats:', e);
    }
  }, []);

  const loadCrewForPartners = async (partnerIds) => {
    if (!partnerIds.length) {
      setCrewByPartner({});
      return;
    }
    try {
      const chunkSize = 80;
      const map = {};
      for (let i = 0; i < partnerIds.length; i += chunkSize) {
        const chunk = partnerIds.slice(i, i + chunkSize);
        const url =
          `${SUPABASE_URL}/rest/v1/partner_crew_members` +
          `?partner_id=in.(${chunk.join(',')})` +
          `&select=id,partner_id,display_name,phone,status,can_negotiate_rates,can_collect_payment` +
          `&order=created_at.asc&limit=2000`;
        const response = await fetch(url, { headers: authHeaders });
        const data = await response.json();
        if (!Array.isArray(data)) continue;
        data.forEach((row) => {
          if (!map[row.partner_id]) map[row.partner_id] = [];
          map[row.partner_id].push(row);
        });
      }
      setCrewByPartner(map);
    } catch (e) {
      console.error('Error loading crew:', e);
      setCrewByPartner({});
    }
  };

  const loadTicketBalances = async (partnerIds) => {
    if (!partnerIds.length) {
      setTicketsByPartner({});
      return;
    }
    try {
      const chunkSize = 80;
      const map = {};
      for (let i = 0; i < partnerIds.length; i += chunkSize) {
        const chunk = partnerIds.slice(i, i + chunkSize);
        const url =
          `${SUPABASE_URL}/rest/v1/partner_ticket_balances` +
          `?mechanic_id=in.(${chunk.join(',')})` +
          `&select=mechanic_id,available,reserved` +
          `&limit=5000`;
        const response = await fetch(url, { headers: authHeaders });
        const data = await response.json();
        if (!Array.isArray(data)) continue;
        data.forEach((row) => {
          if (!map[row.mechanic_id]) map[row.mechanic_id] = { available: 0, reserved: 0 };
          map[row.mechanic_id].available += Number(row.available || 0);
          map[row.mechanic_id].reserved += Number(row.reserved || 0);
        });
      }
      setTicketsByPartner(map);
    } catch (e) {
      console.error('Error loading ticket balances:', e);
      setTicketsByPartner({});
    }
  };

  const loadMechanics = useCallback(async () => {
    try {
      setLoading(true);
      let order = 'created_at.desc';
      if (sortBy === 'previous') order = 'previous_assigned_cases.desc.nullslast,created_at.desc';
      if (sortBy === 'name') order = 'mechanic_name.asc.nullslast';

      const from = (page - 1) * pageSize;
      const to = from + pageSize - 1;

      const params = new URLSearchParams();
      params.set(
        'select',
        'id,code,mechanic_name,shop_name,number,alternate_number1,partner_type,roadserve_commission_percent,owner_user_id,active,previous_assigned_cases,kyc_status,created_at',
      );
      params.set('order', order);

      params.set('merged_into_id', 'is.null');
      if (filterActive !== 'all') {
        params.set('active', `eq.${filterActive === 'active'}`);
      }
      if (filterLinked === 'linked') {
        params.set('owner_user_id', 'not.is.null');
      } else if (filterLinked === 'unlinked') {
        params.set('owner_user_id', 'is.null');
      }

      const q = searchQuery.trim();
      const searchOr = (() => {
        if (!q) return '';
        const safe = q.replace(/[%(),]/g, '');
        const orParts = [
          `mechanic_name.ilike.*${safe}*`,
          `code.ilike.*${safe}*`,
          `number.ilike.*${safe}*`,
          `alternate_number1.ilike.*${safe}*`,
        ];
        if (/^[0-9a-f-]{8,}$/i.test(safe)) {
          orParts.push(`owner_user_id.eq.${safe}`);
        }
        return `(${orParts.join(',')})`;
      })();
      const unverifiedOr = '(kyc_status.is.null,kyc_status.neq.verified)';

      if (filterKyc === 'verified') {
        params.set('kyc_status', 'eq.verified');
        if (searchOr) params.set('or', searchOr);
      } else if (filterKyc === 'unverified') {
        if (searchOr) {
          params.set('and', `(or${searchOr},or${unverifiedOr})`);
        } else {
          params.set('or', unverifiedOr);
        }
      } else if (searchOr) {
        params.set('or', searchOr);
      }

      const response = await fetch(`${SUPABASE_URL}/rest/v1/mechanics?${params.toString()}`, {
        headers: {
          ...authHeaders,
          Prefer: 'count=exact',
          Range: `${from}-${to}`,
        },
      });
      let data = await response.json();
      if (!Array.isArray(data) && params.has('merged_into_id')) {
        params.delete('merged_into_id');
        const retry = await fetch(`${SUPABASE_URL}/rest/v1/mechanics?${params.toString()}`, {
          headers: {
            ...authHeaders,
            Prefer: 'count=exact',
            Range: `${from}-${to}`,
          },
        });
        data = await retry.json();
        setTotalCount(parseContentRangeTotal(retry.headers.get('content-range')));
      } else {
        setTotalCount(parseContentRangeTotal(response.headers.get('content-range')));
      }
      const list = Array.isArray(data) ? data : [];
      setMechanics(list);
      setExpandedTeamId(null);
      const ids = list.map((m) => m.id);
      await Promise.all([loadCrewForPartners(ids), loadTicketBalances(ids)]);
    } catch (error) {
      console.error('Error loading mechanics:', error);
    } finally {
      setLoading(false);
    }
  }, [filterActive, filterLinked, filterKyc, sortBy, page, pageSize, searchQuery]);

  useEffect(() => {
    loadMechanics();
  }, [loadMechanics]);

  useEffect(() => {
    loadStats();
  }, [loadStats]);

  useEffect(() => {
    if (page > totalPages) setPage(totalPages);
  }, [page, totalPages]);

  // Debounce search input → query + reset page
  useEffect(() => {
    const t = setTimeout(() => {
      setPage(1);
      setSearchQuery(searchInput.trim());
    }, 350);
    return () => clearTimeout(t);
  }, [searchInput]);

  useEffect(() => {
    setSelectedIds([]);
  }, [filterActive, filterLinked, filterKyc, searchQuery]);

  const pageNumbers = useMemo(() => {
    const pages = [];
    const maxButtons = 5;
    let start = Math.max(1, page - Math.floor(maxButtons / 2));
    let end = Math.min(totalPages, start + maxButtons - 1);
    start = Math.max(1, end - maxButtons + 1);
    for (let i = start; i <= end; i += 1) pages.push(i);
    return pages;
  }, [page, totalPages]);

  const openDetail = async (mechanicId) => {
    setDetailId(mechanicId);
    setDetailLoading(true);
    setDetail(null);
    setDetailOwner(null);
    setDetailCrew([]);
    setDetailTickets({ available: 0, reserved: 0 });
    try {
      const mechRes = await fetch(
        `${SUPABASE_URL}/rest/v1/mechanics?id=eq.${mechanicId}&select=*&limit=1`,
        { headers: authHeaders },
      );
      const mechRows = await mechRes.json();
      const mech = Array.isArray(mechRows) ? mechRows[0] : null;
      let owner = null;
      if (mech?.owner_user_id) {
        const userRes = await fetch(
          `${SUPABASE_URL}/rest/v1/users?id=eq.${mech.owner_user_id}&select=id,name,email,phone,status,type,role_id,created_at,updated_at&limit=1`,
          { headers: authHeaders },
        );
        const userRows = await userRes.json();
        owner = Array.isArray(userRows) ? userRows[0] || null : null;
      }
      setDetail(mech || null);
      setDetailOwner(owner);
      setDetailForm(seedDetailForm(mech, owner));
      setDetailDirty(false);

      const crewRes = await fetch(
        `${SUPABASE_URL}/rest/v1/partner_crew_members?partner_id=eq.${mechanicId}&select=*&order=created_at.asc&limit=200`,
        { headers: authHeaders },
      );
      const crewRows = await crewRes.json();
      setDetailCrew(Array.isArray(crewRows) ? crewRows : []);

      const ticketRes = await fetch(
        `${SUPABASE_URL}/rest/v1/partner_ticket_balances?mechanic_id=eq.${mechanicId}&select=available,reserved&limit=200`,
        { headers: authHeaders },
      );
      const ticketRows = await ticketRes.json();
      if (Array.isArray(ticketRows)) {
        setDetailTickets({
          available: ticketRows.reduce((s, r) => s + Number(r.available || 0), 0),
          reserved: ticketRows.reduce((s, r) => s + Number(r.reserved || 0), 0),
        });
      }
    } catch (e) {
      console.error('Error loading mechanic detail:', e);
    } finally {
      setDetailLoading(false);
    }
  };

  const closeDetail = () => {
    setDetailId(null);
    setDetail(null);
    setDetailForm(null);
    setDetailDirty(false);
    setDetailOwner(null);
    setDetailCrew([]);
    setDetailTickets({ available: 0, reserved: 0 });
  };

  const requestCloseDetail = () => {
    if (detailDirty && !window.confirm('Discard unsaved changes?')) return;
    closeDetail();
  };

  const setDetailField = (key, value) => {
    setDetailForm((prev) => (prev ? { ...prev, [key]: value } : prev));
    setDetailDirty(true);
  };

  const resetDetailForm = () => {
    if (!detail) return;
    setDetailForm(seedDetailForm(detail, detailOwner));
    setDetailDirty(false);
  };

  const saveDetailProfile = async () => {
    if (!detailId || !detailForm) return;
    setDetailSaving(true);
    try {
      const payload = {
        mechanic_name: String(detailForm.mechanic_name || '').trim() || null,
        shop_name: String(detailForm.shop_name || '').trim() || null,
        code: String(detailForm.code || '').trim() || null,
        number: String(detailForm.number || '').trim() || null,
        alternate_number1: String(detailForm.alternate_number1 || '').trim() || null,
        alternate_number2: String(detailForm.alternate_number2 || '').trim() || null,
        timing: String(detailForm.timing || '').trim() || null,
        partner_type: detailForm.partner_type || 'mechanic',
        city: String(detailForm.city || '').trim() || null,
        area: String(detailForm.area || '').trim() || null,
        state: String(detailForm.state || '').trim() || null,
        district: String(detailForm.district || '').trim() || null,
        service_tag: String(detailForm.service_tag || '').trim() || null,
        service_tag2: String(detailForm.service_tag2 || '').trim() || null,
        service_tag3: String(detailForm.service_tag3 || '').trim() || null,
        experience_years: numOrNull(detailForm.experience_years),
        service_radius_km: numOrNull(detailForm.service_radius_km),
        service_center_lat: numOrNull(detailForm.service_center_lat),
        service_center_lng: numOrNull(detailForm.service_center_lng),
        owner_user_id: String(detailForm.owner_user_id || '').trim() || null,
        public_slug: String(detailForm.public_slug || '').trim() || null,
        is_public: !!detailForm.is_public,
        kyc_status: String(detailForm.kyc_status || '').trim() || null,
        kyc_verified_at: String(detailForm.kyc_verified_at || '').trim() || null,
        kyc_rejection_reason: String(detailForm.kyc_rejection_reason || '').trim() || null,
        logo_url: String(detailForm.logo_url || '').trim() || null,
        bio: String(detailForm.bio || '').trim() || null,
        roadserve_commission_percent: numOrNull(detailForm.roadserve_commission_percent) ?? 20,
        active: !!detailForm.active,
        previous_assigned_cases: numOrNull(detailForm.previous_assigned_cases) ?? 0,
        city_slug: String(detailForm.city_slug || '').trim() || null,
        area_slug: String(detailForm.area_slug || '').trim() || null,
        auto_online_by_slots: !!detailForm.auto_online_by_slots,
      };
      const ownerPatch = {
        name: String(detailForm.owner_name || '').trim() || null,
        email: String(detailForm.owner_email || '').trim() || null,
        phone: String(detailForm.owner_phone || '').trim() || null,
        status: String(detailForm.owner_status || '').trim() || 'active',
      };
      const { data, error } = await serviceRoleClient.rpc('admin_save_partner_profile', {
        p_mechanic_id: detailId,
        p_mechanic: payload,
        p_owner: ownerPatch,
      });
      if (error) {
        alert(
          /could not find the function|404/i.test(error.message || '')
            ? 'Run supabase/admin-save-partner-profile.sql in Supabase SQL Editor, then Save again.'
            : error.message || 'Failed to save partner',
        );
        return;
      }
      const row = data && typeof data === 'object' && !Array.isArray(data) ? data : data?.[0];
      if (!row?.success || !row?.mechanic?.id) {
        alert(row?.error || 'Partner row did not update. Try again.');
        return;
      }

      setDetail(row.mechanic);
      setDetailOwner(row.owner || null);
      setDetailForm(seedDetailForm(row.mechanic, row.owner || null));
      setDetailDirty(false);
      loadMechanics();
      loadStats();
    } catch (e) {
      alert(e.message || 'Failed to save partner');
    } finally {
      setDetailSaving(false);
    }
  };

  const toggleStatus = async (mechanicId, currentStatus) => {
    try {
      const response = await fetch(`${SUPABASE_URL}/rest/v1/mechanics?id=eq.${mechanicId}`, {
        method: 'PATCH',
        headers: {
          ...authHeaders,
          'Content-Type': 'application/json',
          Prefer: 'return=representation',
        },
        body: JSON.stringify({ active: !currentStatus }),
      });

      if (response.ok) {
        loadMechanics();
        loadStats();
      }
    } catch (error) {
      console.error('Error toggling status:', error);
    }
  };

  const pageIds = useMemo(() => mechanics.map((m) => m.id), [mechanics]);
  const allPageSelected = pageIds.length > 0 && pageIds.every((id) => selectedIds.includes(id));
  const somePageSelected = pageIds.some((id) => selectedIds.includes(id));

  const toggleSelectOne = (id) => {
    setSelectedIds((prev) => (prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]));
  };

  const toggleSelectPage = () => {
    setSelectedIds((prev) => {
      if (allPageSelected) return prev.filter((id) => !pageIds.includes(id));
      const next = new Set(prev);
      pageIds.forEach((id) => next.add(id));
      return Array.from(next);
    });
  };

  const bulkSetActive = async (active) => {
    if (!selectedIds.length || bulkSaving) return;
    const label = active ? 'Active' : 'Inactive';
    if (!window.confirm(`Set ${selectedIds.length} partner${selectedIds.length === 1 ? '' : 's'} to ${label}?`)) {
      return;
    }
    setBulkSaving(true);
    try {
      const chunkSize = 80;
      for (let i = 0; i < selectedIds.length; i += chunkSize) {
        const chunk = selectedIds.slice(i, i + chunkSize);
        const response = await fetch(
          `${SUPABASE_URL}/rest/v1/mechanics?id=in.(${chunk.join(',')})`,
          {
            method: 'PATCH',
            headers: {
              ...authHeaders,
              'Content-Type': 'application/json',
              Prefer: 'return=minimal',
            },
            body: JSON.stringify({ active }),
          },
        );
        if (!response.ok) {
          const text = await response.text();
          throw new Error(text || `Bulk update failed (${response.status})`);
        }
      }
      setSelectedIds([]);
      await Promise.all([loadMechanics(), loadStats()]);
    } catch (error) {
      console.error('Error bulk updating status:', error);
      alert(error.message || 'Bulk update failed');
    } finally {
      setBulkSaving(false);
    }
  };

  const mergeSelectedPartners = async () => {
    if (selectedIds.length < 2 || bulkSaving) return;
    setBulkSaving(true);
    try {
      let rows = mechanics.filter((m) => selectedIds.includes(m.id));
      const missing = selectedIds.filter((id) => !rows.some((r) => r.id === id));
      if (missing.length) {
        const { data: extra } = await serviceRoleClient
          .from('mechanics')
          .select('id, shop_name, mechanic_name, code, previous_assigned_cases, kyc_status, active')
          .in('id', missing);
        rows = rows.concat(Array.isArray(extra) ? extra : []);
      }
      const ranked = [...rows].sort((a, b) => {
        const prev = Number(b.previous_assigned_cases || 0) - Number(a.previous_assigned_cases || 0);
        if (prev) return prev;
        const kyc = Number(b.kyc_status === 'verified') - Number(a.kyc_status === 'verified');
        if (kyc) return kyc;
        return Number(!!b.active) - Number(!!a.active);
      });
      const keep = ranked[0];
      if (!keep?.id) throw new Error('Could not pick a partner to keep');
      const keepName = keep.shop_name || keep.mechanic_name || keep.code || 'this partner';
      const prevSum = rows.reduce((s, m) => s + Number(m.previous_assigned_cases || 0), 0);
      setBulkSaving(false);
      if (
        !window.confirm(
          `Merge ${selectedIds.length} partners into 1 ID?\n\nKeep: ${keepName} (${keep.code || ''})\nPrevious jobs will add up to ${prevSum}.\nDuplicate partners will be deleted.`,
        )
      ) {
        return;
      }
      setBulkSaving(true);
      const { data, error } = await serviceRoleClient.rpc('admin_merge_mechanics', {
        p_keep_id: keep.id,
        p_source_ids: selectedIds.filter((id) => id !== keep.id),
      });
      if (error) throw error;
      if (data?.success === false) throw new Error(data.error || 'Merge failed');
      setSelectedIds([]);
      await Promise.all([loadMechanics(), loadStats()]);
      alert(`Merged into ${keepName}. Duplicate IDs deleted. Previous jobs now ${Number(data?.previous_assigned_cases ?? prevSum)}.`);
    } catch (error) {
      console.error('Error merging partners:', error);
      const msg = String(error?.message || error || 'Merge failed');
      if (/could not find the function|schema cache|PGRST202|merged_into/i.test(msg)) {
        alert('Pehle Supabase mein supabase/admin-merge-mechanics.sql chalao, phir merge dobara try karo.');
      } else {
        alert(msg);
      }
    } finally {
      setBulkSaving(false);
    }
  };

  const unverifyPartnerKyc = async (mechanic) => {
    if (!mechanic?.id || kycBusyId) return;
    const name = mechanic.shop_name || mechanic.mechanic_name || mechanic.code || 'this partner';
    if (
      !window.confirm(
        `Unverify KYC for ${name}?\n\nPublic page will turn off. Partner can upload KYC documents again.`,
      )
    ) {
      return;
    }
    setKycBusyId(mechanic.id);
    try {
      const { data, error } = await serviceRoleClient.rpc('admin_reset_partner_kyc', {
        p_mechanic_id: mechanic.id,
      });
      if (error) throw error;
      if (data?.success === false) throw new Error(data.error || 'KYC reset failed');
      await loadMechanics();
    } catch (error) {
      const msg = String(error?.message || error || '');
      if (/could not find the function|schema cache|PGRST202/i.test(msg)) {
        const patch = await fetch(`${SUPABASE_URL}/rest/v1/mechanics?id=eq.${mechanic.id}`, {
          method: 'PATCH',
          headers: {
            ...authHeaders,
            'Content-Type': 'application/json',
            Prefer: 'return=minimal',
          },
          body: JSON.stringify({
            kyc_status: 'none',
            is_public: false,
            kyc_rejection_reason: 'Admin reset — re-upload KYC',
          }),
        });
        if (!patch.ok) {
          alert('Pehle supabase/admin-reset-partner-kyc.sql chalao, ya KYC reset fail hua.');
          return;
        }
        await fetch(
          `${SUPABASE_URL}/rest/v1/mechanic_kyc_documents?mechanic_id=eq.${mechanic.id}&status=in.(approved,pending)`,
          {
            method: 'PATCH',
            headers: {
              ...authHeaders,
              'Content-Type': 'application/json',
              Prefer: 'return=minimal',
            },
            body: JSON.stringify({
              status: 'rejected',
              admin_note: 'Admin reset — re-upload KYC',
            }),
          },
        );
        await loadMechanics();
        return;
      }
      alert(msg || 'KYC reset failed');
    } finally {
      setKycBusyId(null);
    }
  };

  const startEdit = (m) => {
    setEditingId(m.id);
    setEditName(m.mechanic_name || m.code || '');
    setEditType(m.partner_type || 'mechanic');
    setEditPercent(String(m.roadserve_commission_percent ?? 20));
    setEditOwner(m.owner_user_id || '');
  };

  const closeEdit = () => {
    if (saving) return;
    setEditingId(null);
  };

  const savePartnerSettings = async () => {
    if (!editingId) return;
    setSaving(true);
    try {
      const response = await fetch(`${SUPABASE_URL}/rest/v1/rpc/admin_update_partner_settings`, {
        method: 'POST',
        headers: {
          ...authHeaders,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          p_mechanic_id: editingId,
          p_partner_type: editType,
          p_roadserve_commission_percent: Number(editPercent),
          p_owner_user_id: editOwner.trim() || null,
        }),
      });
      const data = await response.json();
      if (!response.ok || data?.success === false) {
        alert(data?.error || 'Failed to save');
      } else {
        setEditingId(null);
        loadMechanics();
      }
    } catch (e) {
      alert(e.message || 'Failed');
    } finally {
      setSaving(false);
    }
  };

  const loadCreditGrants = async (mechanicId) => {
    setCreditLoading(true);
    try {
      const response = await fetch(`${SUPABASE_URL}/rest/v1/rpc/admin_list_partner_credits`, {
        method: 'POST',
        headers: { ...authHeaders, 'Content-Type': 'application/json' },
        body: JSON.stringify({ p_mechanic_id: mechanicId, p_limit: 30 }),
      });
      const data = await response.json();
      setCreditRows(Array.isArray(data) ? data : []);

      const balance = await fetch(`${SUPABASE_URL}/rest/v1/rpc/partner_get_wallet_balance`, {
        method: 'POST',
        headers: { ...authHeaders, 'Content-Type': 'application/json' },
        body: JSON.stringify({ p_mechanic_id: mechanicId }),
      });
      const balanceData = await balance.json();
      setCreditBalance(Number(balanceData?.wallet_available || balanceData?.available_credits || 0));
    } catch {
      setCreditRows([]);
      setCreditBalance(0);
    } finally {
      setCreditLoading(false);
    }
  };

  const openCredits = (m) => {
    setCreditPartner(m);
    setCreditAmount('');
    setCreditRepayable(false);
    setCreditNote('');
    setCreditRows([]);
    setCreditBalance(0);
    loadCreditGrants(m.id);
  };

  const closeCredits = () => {
    if (creditSaving) return;
    setCreditPartner(null);
  };

  const loadTicketHistory = async (mechanicId) => {
    setTicketHistoryLoading(true);
    setTicketHistoryPurchases([]);
    setTicketHistoryHolds([]);
    setTicketHistoryBalance(null);
    try {
      const [purRes, prodRes, holdRes, balRes] = await Promise.all([
        fetch(
          `${SUPABASE_URL}/rest/v1/partner_ticket_purchases?mechanic_id=eq.${mechanicId}` +
            `&select=id,product_id,tickets_credited,amount_inr,status,created_at,paid_at` +
            `&order=created_at.desc&limit=80`,
          { headers: authHeaders },
        ),
        fetch(
          `${SUPABASE_URL}/rest/v1/partner_ticket_products?select=id,name&limit=200`,
          { headers: authHeaders },
        ),
        fetch(
          `${SUPABASE_URL}/rest/v1/partner_ticket_holds?mechanic_id=eq.${mechanicId}` +
            `&select=id,status,created_at,updated_at,service_request_id,product_id` +
            `&order=created_at.desc&limit=80`,
          { headers: authHeaders },
        ),
        fetch(`${SUPABASE_URL}/rest/v1/rpc/partner_get_ticket_balance`, {
          method: 'POST',
          headers: { ...authHeaders, 'Content-Type': 'application/json' },
          body: JSON.stringify({ p_mechanic_id: mechanicId, p_service_type: null }),
        }),
      ]);
      const purchases = await purRes.json();
      const products = await prodRes.json();
      const holds = await holdRes.json();
      const balance = await balRes.json();
      const productName = {};
      if (Array.isArray(products)) {
        products.forEach((p) => {
          productName[p.id] = p.name;
        });
      }

      const purchaseRows = Array.isArray(purchases)
        ? purchases.map((row) => ({
            ...row,
            product_name: productName[row.product_id] || 'Ticket pack',
          }))
        : [];
      setTicketHistoryPurchases(purchaseRows);

      const holdRows = Array.isArray(holds) ? holds : [];
      const requestIds = [...new Set(holdRows.map((h) => h.service_request_id).filter(Boolean))];
      const requestMap = {};
      if (requestIds.length) {
        const reqRes = await fetch(
          `${SUPABASE_URL}/rest/v1/service_requests?id=in.(${requestIds.join(',')})` +
            `&select=id,status,service_type&limit=200`,
          { headers: authHeaders },
        );
        const reqRows = await reqRes.json();
        if (Array.isArray(reqRows)) {
          reqRows.forEach((r) => {
            requestMap[r.id] = r;
          });
        }
      }
      setTicketHistoryHolds(
        holdRows.map((row) => ({
          ...row,
          product_name: productName[row.product_id] || 'Ticket',
          request: requestMap[row.service_request_id] || null,
        })),
      );

      setTicketHistoryBalance(
        balance && typeof balance === 'object' && !Array.isArray(balance) ? balance : null,
      );
    } catch (e) {
      console.error('Error loading ticket history:', e);
    } finally {
      setTicketHistoryLoading(false);
    }
  };

  const openTicketHistory = (m) => {
    setTicketHistoryPartner(m);
    setTicketHistoryTab('purchases');
    loadTicketHistory(m.id);
  };

  const closeTicketHistory = () => {
    setTicketHistoryPartner(null);
    setTicketHistoryPurchases([]);
    setTicketHistoryHolds([]);
    setTicketHistoryBalance(null);
  };

  const grantCredits = async () => {
    if (!creditPartner?.id) return;
    const amount = Number(creditAmount);
    if (!Number.isFinite(amount) || amount <= 0) {
      alert('Enter a valid credit amount');
      return;
    }
    setCreditSaving(true);
    try {
      const response = await fetch(`${SUPABASE_URL}/rest/v1/rpc/admin_grant_partner_credits`, {
        method: 'POST',
        headers: { ...authHeaders, 'Content-Type': 'application/json' },
        body: JSON.stringify({
          p_mechanic_id: creditPartner.id,
          p_amount: amount,
          p_repayable: creditRepayable,
          p_note: creditNote.trim() || null,
        }),
      });
      const data = await response.json();
      if (!response.ok || data?.success === false) {
        alert(data?.error || 'Could not add credits');
        return;
      }
      setCreditAmount('');
      setCreditNote('');
      await loadCreditGrants(creditPartner.id);
    } catch (e) {
      alert(e.message || 'Could not add credits');
    } finally {
      setCreditSaving(false);
    }
  };

  const creditOutstanding = creditRows.reduce(
    (sum, row) => sum + Number(row.outstanding || 0),
    0,
  );
  const creditAvailable = Number(
    creditBalance || creditRows[0]?.available_credits || 0,
  );

  return (
    <div className="mx-auto w-full max-w-[1480px] space-y-5">
      <div className="flex flex-col gap-3 sm:flex-row sm:items-end sm:justify-between">
        <div>
          <h1 className="text-2xl font-bold tracking-tight text-slate-900">Partner Management</h1>
          <p className="mt-0.5 text-sm text-slate-500">
            Mechanics &amp; towing — previous completed jobs, team, login link, available tickets
          </p>
        </div>
        <div className="flex items-center gap-2 self-start">
          <button
            type="button"
            onClick={() => setCoverageOpen(true)}
            className="inline-flex items-center gap-2 rounded-lg bg-indigo-600 px-3 py-2 text-xs font-semibold text-white shadow-sm hover:bg-indigo-700"
          >
            <MapPin size={14} />
            Check Coverage
          </button>
          <button
            type="button"
            onClick={() => {
              loadMechanics();
              loadStats();
            }}
            disabled={loading}
            className="inline-flex items-center gap-2 rounded-lg border border-slate-200 bg-white px-3 py-2 text-xs font-semibold text-slate-700 shadow-sm hover:bg-slate-50 disabled:opacity-50"
          >
            <RefreshCw size={14} className={loading ? 'animate-spin' : ''} />
            Refresh
          </button>
        </div>
      </div>

      <div className="grid grid-cols-2 gap-3 lg:grid-cols-4">
        <div className="rounded-xl border border-slate-200 bg-white p-3.5 shadow-sm">
          <p className="text-[11px] font-semibold uppercase tracking-wide text-slate-500">Partners</p>
          <p className="mt-1 text-2xl font-bold tabular-nums text-slate-900">{stats.total || totalCount}</p>
          <p className="mt-0.5 text-xs text-slate-500">
            {stats.active} active · {stats.linked} linked
          </p>
        </div>
        <div className="rounded-xl border border-slate-200 bg-white p-3.5 shadow-sm">
          <p className="text-[11px] font-semibold uppercase tracking-wide text-slate-500">
            Previous completed
          </p>
          <p className="mt-1 text-2xl font-bold tabular-nums text-slate-900">{stats.prevSum}</p>
          <p className="mt-0.5 text-xs text-slate-500">{stats.withPrev} partners with history</p>
        </div>
        <div className="rounded-xl border border-slate-200 bg-white p-3.5 shadow-sm">
          <p className="text-[11px] font-semibold uppercase tracking-wide text-slate-500">Teams</p>
          <p className="mt-1 text-2xl font-bold tabular-nums text-slate-900">{stats.withTeam}</p>
          <p className="mt-0.5 text-xs text-slate-500">{stats.teamMembers} crew members total</p>
        </div>
        <div className="rounded-xl border border-indigo-100 bg-gradient-to-br from-indigo-50 to-white p-3.5 shadow-sm">
          <p className="text-[11px] font-semibold uppercase tracking-wide text-indigo-600/80">Tip</p>
          <p className="mt-1 text-sm font-medium leading-snug text-slate-800">
            Phone OTP auto-links login. Team members login with their own number under main partner.
          </p>
        </div>
      </div>

      <div className="overflow-hidden rounded-xl border border-slate-200 bg-white shadow-sm">
        <div className="flex flex-col gap-3 border-b border-slate-100 p-3 lg:flex-row lg:items-center lg:gap-3 lg:px-4">
          <div className="relative min-w-0 flex-1">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" size={16} />
            <input
              type="text"
              placeholder="Search name, code, phone, or owner id..."
              value={searchInput}
              onChange={(e) => setSearchInput(e.target.value)}
              className="w-full rounded-lg border border-slate-200 bg-slate-50/50 py-2 pl-9 pr-3 text-sm text-slate-900 placeholder:text-slate-400 focus:border-indigo-300 focus:bg-white focus:outline-none focus:ring-2 focus:ring-indigo-500/20"
            />
          </div>
          <div className="flex flex-wrap items-center gap-2">
            <div className="flex shrink-0 gap-1 rounded-lg border border-slate-200 bg-slate-50 p-0.5">
              {['all', 'active', 'inactive'].map((key) => (
                <button
                  key={key}
                  type="button"
                  onClick={() => {
                    setPage(1);
                    setFilterActive(key);
                  }}
                  className={`rounded-md px-3 py-1.5 text-xs font-semibold capitalize transition ${
                    filterActive === key
                      ? 'bg-indigo-600 text-white shadow-sm'
                      : 'text-slate-600 hover:bg-white'
                  }`}
                >
                  {key}
                </button>
              ))}
            </div>
            <div className="flex shrink-0 gap-1 rounded-lg border border-slate-200 bg-slate-50 p-0.5">
              {[
                { id: 'all', label: 'All login' },
                { id: 'linked', label: 'Linked' },
                { id: 'unlinked', label: 'Not linked' },
              ].map((opt) => (
                <button
                  key={opt.id}
                  type="button"
                  onClick={() => {
                    setPage(1);
                    setFilterLinked(opt.id);
                  }}
                  className={`rounded-md px-3 py-1.5 text-xs font-semibold transition ${
                    filterLinked === opt.id
                      ? 'bg-emerald-600 text-white shadow-sm'
                      : 'text-slate-600 hover:bg-white'
                  }`}
                >
                  {opt.label}
                </button>
              ))}
            </div>
            <div className="flex shrink-0 gap-1 rounded-lg border border-slate-200 bg-slate-50 p-0.5">
              {[
                { id: 'all', label: 'All KYC' },
                { id: 'verified', label: 'Verified' },
                { id: 'unverified', label: 'Unverified' },
              ].map((opt) => (
                <button
                  key={opt.id}
                  type="button"
                  onClick={() => {
                    setPage(1);
                    setFilterKyc(opt.id);
                  }}
                  className={`rounded-md px-3 py-1.5 text-xs font-semibold transition ${
                    filterKyc === opt.id
                      ? 'bg-violet-600 text-white shadow-sm'
                      : 'text-slate-600 hover:bg-white'
                  }`}
                >
                  {opt.label}
                </button>
              ))}
            </div>
            <select
              value={sortBy}
              onChange={(e) => {
                setPage(1);
                setSortBy(e.target.value);
              }}
              className="rounded-lg border border-slate-200 bg-white px-2.5 py-1.5 text-xs font-semibold text-slate-700 focus:border-indigo-300 focus:outline-none focus:ring-2 focus:ring-indigo-500/20"
            >
              <option value="previous">Sort: Previous jobs</option>
              <option value="newest">Sort: Newest</option>
              <option value="name">Sort: Name</option>
            </select>
            <select
              value={pageSize}
              onChange={(e) => {
                setPage(1);
                setPageSize(Number(e.target.value));
              }}
              className="rounded-lg border border-slate-200 bg-white px-2.5 py-1.5 text-xs font-semibold text-slate-700 focus:border-indigo-300 focus:outline-none focus:ring-2 focus:ring-indigo-500/20"
            >
              {PAGE_SIZE_OPTIONS.map((n) => (
                <option key={n} value={n}>
                  {n} / page
                </option>
              ))}
            </select>
          </div>
        </div>

        {selectedIds.length > 0 ? (
          <div className="flex flex-col gap-2 border-b border-indigo-100 bg-indigo-50/80 px-4 py-2.5 sm:flex-row sm:items-center sm:justify-between">
            <p className="text-xs font-semibold text-indigo-800">
              {selectedIds.length} selected
              {somePageSelected && !allPageSelected ? ' · this page partially selected' : ''}
            </p>
            <div className="flex flex-wrap items-center gap-2">
              <button
                type="button"
                disabled={bulkSaving}
                onClick={() => bulkSetActive(true)}
                className="inline-flex items-center gap-1.5 rounded-lg bg-emerald-600 px-3 py-1.5 text-xs font-semibold text-white hover:bg-emerald-700 disabled:opacity-50"
              >
                {bulkSaving ? <Loader2 size={13} className="animate-spin" /> : <Power size={13} />}
                Set Active
              </button>
              <button
                type="button"
                disabled={bulkSaving}
                onClick={() => bulkSetActive(false)}
                className="inline-flex items-center gap-1.5 rounded-lg bg-red-600 px-3 py-1.5 text-xs font-semibold text-white hover:bg-red-700 disabled:opacity-50"
              >
                {bulkSaving ? <Loader2 size={13} className="animate-spin" /> : <Power size={13} />}
                Set Inactive
              </button>
              <button
                type="button"
                disabled={bulkSaving || selectedIds.length < 2}
                onClick={mergeSelectedPartners}
                className="inline-flex items-center gap-1.5 rounded-lg bg-indigo-600 px-3 py-1.5 text-xs font-semibold text-white hover:bg-indigo-700 disabled:opacity-50"
              >
                {bulkSaving ? <Loader2 size={13} className="animate-spin" /> : <GitMerge size={13} />}
                Merge into 1 ID
              </button>
              <button
                type="button"
                disabled={bulkSaving}
                onClick={() => setSelectedIds([])}
                className="rounded-lg border border-indigo-200 bg-white px-3 py-1.5 text-xs font-semibold text-indigo-700 hover:bg-indigo-50 disabled:opacity-50"
              >
                Clear
              </button>
            </div>
          </div>
        ) : null}

        {loading ? (
          <div className="flex items-center justify-center gap-2 p-12 text-sm text-slate-500">
            <Loader2 className="animate-spin" size={18} />
            Loading partners…
          </div>
        ) : mechanics.length === 0 ? (
          <div className="p-12 text-center text-sm text-slate-500">No partners found</div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full min-w-[1100px] border-collapse text-left">
              <thead>
                <tr className="border-b border-slate-100 bg-slate-50/80">
                  <th className="w-10 px-3 py-3">
                    <input
                      type="checkbox"
                      checked={allPageSelected}
                      ref={(el) => {
                        if (el) el.indeterminate = somePageSelected && !allPageSelected;
                      }}
                      onChange={toggleSelectPage}
                      aria-label="Select all on this page"
                      className="h-3.5 w-3.5 rounded border-slate-300 text-indigo-600 focus:ring-indigo-500"
                    />
                  </th>
                  <th className="whitespace-nowrap px-4 py-3 text-[11px] font-semibold uppercase tracking-wide text-slate-500">
                    Partner
                  </th>
                  <th className="whitespace-nowrap px-3 py-3 text-[11px] font-semibold uppercase tracking-wide text-slate-500">
                    Type
                  </th>
                  <th className="whitespace-nowrap px-3 py-3 text-[11px] font-semibold uppercase tracking-wide text-slate-500">
                    Previous
                  </th>
                  <th className="min-w-[180px] px-3 py-3 text-[11px] font-semibold uppercase tracking-wide text-slate-500">
                    Team
                  </th>
                  <th className="whitespace-nowrap px-3 py-3 text-[11px] font-semibold uppercase tracking-wide text-slate-500">
                    Phone
                  </th>
                  <th className="whitespace-nowrap px-3 py-3 text-[11px] font-semibold uppercase tracking-wide text-slate-500">
                    Login
                  </th>
                  <th className="whitespace-nowrap px-3 py-3 text-[11px] font-semibold uppercase tracking-wide text-slate-500">
                    KYC
                  </th>
                  <th className="whitespace-nowrap px-3 py-3 text-[11px] font-semibold uppercase tracking-wide text-slate-500">
                    Tickets
                  </th>
                  <th className="whitespace-nowrap px-3 py-3 text-[11px] font-semibold uppercase tracking-wide text-slate-500">
                    Status
                  </th>
                  <th className="sticky right-0 z-10 whitespace-nowrap bg-slate-50/95 px-4 py-3 text-right text-[11px] font-semibold uppercase tracking-wide text-slate-500 shadow-[-8px_0_12px_-8px_rgba(15,23,42,0.12)]">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {mechanics.map((mechanic) => {
                  const linked = Boolean(mechanic.owner_user_id);
                  const prev = Number(mechanic.previous_assigned_cases || 0);
                  const crew = crewByPartner[mechanic.id] || [];
                  const activeCrew = crew.filter((c) => (c.status || 'active') === 'active');
                  const teamOpen = expandedTeamId === mechanic.id;
                  const typeKey = mechanic.partner_type || 'mechanic';
                  const kycVerified = String(mechanic.kyc_status || '').toLowerCase() === 'verified';
                  const tickets = ticketsByPartner[mechanic.id] || { available: 0, reserved: 0 };
                  const ticketAvail = Number(tickets.available || 0);

                  return (
                    <React.Fragment key={mechanic.id}>
                      <tr
                        className="group cursor-pointer hover:bg-indigo-50/50"
                        onClick={() => openDetail(mechanic.id)}
                      >
                        <td
                          className="px-3 py-3"
                          onClick={(e) => e.stopPropagation()}
                        >
                          <input
                            type="checkbox"
                            checked={selectedIds.includes(mechanic.id)}
                            onChange={() => toggleSelectOne(mechanic.id)}
                            aria-label={`Select ${mechanic.mechanic_name || mechanic.code || 'partner'}`}
                            className="h-3.5 w-3.5 rounded border-slate-300 text-indigo-600 focus:ring-indigo-500"
                          />
                        </td>
                        <td className="px-4 py-3" onClick={(e) => e.stopPropagation()}>
                          <button
                            type="button"
                            onClick={() =>
                              setPreviewPartner({
                                mechanic_id: mechanic.id,
                                shop_name: mechanic.shop_name || mechanic.mechanic_name || mechanic.code,
                                phone: mechanic.number,
                              })
                            }
                            className="inline-flex max-w-[220px] items-center gap-1.5 text-left text-sm font-semibold text-slate-900 underline decoration-slate-300 underline-offset-2 hover:text-indigo-700 hover:decoration-indigo-400"
                            title="Open partner app preview"
                          >
                            <span className="truncate">{mechanic.mechanic_name || '—'}</span>
                            <Smartphone size={13} className="shrink-0 text-indigo-500" />
                          </button>
                          <p className="mt-0.5 font-mono text-[11px] font-medium text-slate-500">
                            {mechanic.code || '—'}
                          </p>
                        </td>
                        <td className="whitespace-nowrap px-3 py-3">
                          <span
                            className={`inline-flex rounded-md px-2 py-0.5 text-xs font-semibold ring-1 ring-inset ${
                              TYPE_BADGE[typeKey] || TYPE_BADGE.mechanic
                            }`}
                          >
                            {TYPE_LABEL[typeKey] || typeKey}
                          </span>
                        </td>
                        <td className="whitespace-nowrap px-3 py-3">
                          <span
                            className={`inline-flex items-center gap-1 rounded-md px-2 py-1 text-xs font-bold tabular-nums ring-1 ring-inset ${
                              prev > 0
                                ? 'bg-emerald-50 text-emerald-800 ring-emerald-600/15'
                                : 'bg-slate-50 text-slate-500 ring-slate-200'
                            }`}
                            title="ZIP + DB completed jobs (previous_assigned_cases)"
                          >
                            <Briefcase size={12} />
                            {prev}
                          </span>
                        </td>
                        <td className="px-3 py-3">
                          {crew.length === 0 ? (
                            <span className="text-xs text-slate-400">No team</span>
                          ) : (
                            <button
                              type="button"
                              onClick={(e) => {
                                e.stopPropagation();
                                setExpandedTeamId(teamOpen ? null : mechanic.id);
                              }}
                              className="inline-flex max-w-full items-center gap-1.5 rounded-lg border border-slate-200 bg-slate-50 px-2 py-1 text-left text-xs font-semibold text-slate-700 hover:border-indigo-200 hover:bg-indigo-50 hover:text-indigo-800"
                            >
                              <Users size={13} className="shrink-0 text-indigo-600" />
                              <span>
                                {activeCrew.length} member{activeCrew.length === 1 ? '' : 's'}
                                {crew.length !== activeCrew.length
                                  ? ` · ${crew.length - activeCrew.length} off`
                                  : ''}
                              </span>
                            </button>
                          )}
                        </td>
                        <td className="whitespace-nowrap px-3 py-3">
                          <span className="inline-flex items-center gap-1 text-sm tabular-nums text-slate-700">
                            <Phone size={12} className="text-slate-400" />
                            {mechanic.number || '—'}
                          </span>
                        </td>
                        <td className="px-3 py-3">
                          {linked ? (
                            <span
                              className="inline-flex max-w-[120px] items-center gap-1 truncate rounded-md bg-emerald-50 px-2 py-0.5 text-[11px] font-semibold text-emerald-700"
                              title={mechanic.owner_user_id}
                            >
                              <Link2 size={12} className="shrink-0" />
                              Linked
                            </span>
                          ) : (
                            <span className="inline-flex rounded-md bg-amber-50 px-2 py-0.5 text-[11px] font-semibold text-amber-700">
                              Not linked
                            </span>
                          )}
                        </td>
                        <td
                          className="whitespace-nowrap px-3 py-3"
                          onClick={(e) => e.stopPropagation()}
                        >
                          {kycVerified ? (
                            <button
                              type="button"
                              disabled={kycBusyId === mechanic.id}
                              onClick={() => unverifyPartnerKyc(mechanic)}
                              className="inline-flex items-center gap-1 rounded-md bg-emerald-50 px-2 py-0.5 text-[11px] font-semibold text-emerald-700 hover:bg-amber-50 hover:text-amber-800 disabled:opacity-50"
                              title="Click to unverify — partner can upload KYC again"
                            >
                              <ShieldCheck size={12} className="shrink-0" />
                              {kycBusyId === mechanic.id ? '…' : 'Verified'}
                            </button>
                          ) : (
                            <span
                              className="inline-flex items-center gap-1 rounded-md bg-slate-100 px-2 py-0.5 text-[11px] font-semibold text-slate-600"
                              title={mechanic.kyc_status || 'KYC not verified'}
                            >
                              <ShieldAlert size={12} className="shrink-0" />
                              Unverified
                            </span>
                          )}
                        </td>
                        <td
                          className="whitespace-nowrap px-3 py-3"
                          onClick={(e) => e.stopPropagation()}
                        >
                          <button
                            type="button"
                            onClick={() => openTicketHistory(mechanic)}
                            className={`inline-flex items-center gap-1 rounded-md px-2 py-1 text-xs font-bold tabular-nums ring-1 ring-inset hover:ring-2 ${
                              ticketAvail > 0
                                ? 'bg-amber-50 text-amber-900 ring-amber-600/15 hover:bg-amber-100'
                                : 'bg-slate-50 text-slate-500 ring-slate-200 hover:bg-slate-100'
                            }`}
                            title="View ticket history"
                          >
                            <Ticket size={12} />
                            {ticketAvail}
                          </button>
                        </td>
                        <td className="whitespace-nowrap px-3 py-3">
                          <span
                            className={`inline-flex items-center gap-1.5 rounded-full px-2.5 py-1 text-xs font-semibold ring-1 ring-inset ${
                              mechanic.active
                                ? 'bg-emerald-50 text-emerald-700 ring-emerald-600/15'
                                : 'bg-red-50 text-red-700 ring-red-600/15'
                            }`}
                          >
                            <span
                              className={`h-1.5 w-1.5 rounded-full ${
                                mechanic.active ? 'bg-emerald-500' : 'bg-red-500'
                              }`}
                            />
                            {mechanic.active ? 'Active' : 'Inactive'}
                          </span>
                        </td>
                        <td
                          className="sticky right-0 z-10 whitespace-nowrap bg-white px-4 py-3 text-right shadow-[-8px_0_12px_-8px_rgba(15,23,42,0.12)] group-hover:bg-indigo-50/50"
                          onClick={(e) => e.stopPropagation()}
                        >
                          <div className="inline-flex items-center gap-1">
                            <button
                              type="button"
                              onClick={() => startEdit(mechanic)}
                              className="inline-flex items-center gap-1 rounded-lg px-2.5 py-1.5 text-xs font-semibold text-indigo-700 hover:bg-indigo-50"
                            >
                              <Pencil size={13} />
                              Edit
                            </button>
                            <button
                              type="button"
                              onClick={() => openCredits(mechanic)}
                              className="inline-flex items-center gap-1 rounded-lg px-2.5 py-1.5 text-xs font-semibold text-emerald-700 hover:bg-emerald-50"
                              title="Give wallet credits"
                            >
                              <Wallet size={13} />
                              Credits
                            </button>
                            <button
                              type="button"
                              onClick={() => toggleStatus(mechanic.id, mechanic.active)}
                              className="inline-flex items-center gap-1 rounded-lg px-2.5 py-1.5 text-xs font-semibold text-slate-600 hover:bg-slate-100"
                              title={mechanic.active ? 'Deactivate' : 'Activate'}
                            >
                              <Power size={13} />
                              {mechanic.active ? 'Off' : 'On'}
                            </button>
                          </div>
                        </td>
                      </tr>
                      {teamOpen && crew.length > 0 ? (
                        <tr className="bg-indigo-50/40">
                          <td colSpan={11} className="px-4 py-3">
                            <div className="rounded-lg border border-indigo-100 bg-white/80 p-3">
                              <p className="mb-2 text-xs font-semibold uppercase tracking-wide text-indigo-700">
                                Team under {mechanic.mechanic_name || mechanic.code}
                              </p>
                              <div className="grid gap-2 sm:grid-cols-2 lg:grid-cols-3">
                                {crew.map((member) => (
                                  <div
                                    key={member.id}
                                    className="rounded-lg border border-slate-200 bg-white px-3 py-2"
                                  >
                                    <p className="truncate text-sm font-semibold text-slate-900">
                                      {member.display_name || 'Crew member'}
                                    </p>
                                    <p className="mt-0.5 text-xs tabular-nums text-slate-500">
                                      {member.phone || '—'}
                                    </p>
                                    <div className="mt-1.5 flex flex-wrap gap-1">
                                      <span
                                        className={`rounded px-1.5 py-0.5 text-[10px] font-semibold ${
                                          (member.status || 'active') === 'active'
                                            ? 'bg-emerald-50 text-emerald-700'
                                            : 'bg-slate-100 text-slate-500'
                                        }`}
                                      >
                                        {member.status || 'active'}
                                      </span>
                                      {member.can_negotiate_rates ? (
                                        <span className="rounded bg-sky-50 px-1.5 py-0.5 text-[10px] font-semibold text-sky-700">
                                          Negotiate
                                        </span>
                                      ) : null}
                                      {member.can_collect_payment ? (
                                        <span className="rounded bg-amber-50 px-1.5 py-0.5 text-[10px] font-semibold text-amber-700">
                                          Collect
                                        </span>
                                      ) : null}
                                    </div>
                                  </div>
                                ))}
                              </div>
                            </div>
                          </td>
                        </tr>
                      ) : null}
                    </React.Fragment>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}

        {!loading ? (
          <div className="flex flex-col gap-3 border-t border-slate-100 px-4 py-3 sm:flex-row sm:items-center sm:justify-between">
            <p className="text-xs text-slate-500">
              {totalCount === 0
                ? 'No partners'
                : `Showing ${(page - 1) * pageSize + 1}–${Math.min(page * pageSize, totalCount)} of ${totalCount} partners`}
              {' · '}Click a row for full detail
            </p>
            <div className="flex flex-wrap items-center gap-1">
              <button
                type="button"
                disabled={page <= 1 || loading}
                onClick={() => setPage((p) => Math.max(1, p - 1))}
                className="inline-flex items-center gap-1 rounded-lg border border-slate-200 bg-white px-2.5 py-1.5 text-xs font-semibold text-slate-700 hover:bg-slate-50 disabled:cursor-not-allowed disabled:opacity-40"
              >
                <ChevronLeft size={14} />
                Prev
              </button>
              {pageNumbers.map((n) => (
                <button
                  key={n}
                  type="button"
                  onClick={() => setPage(n)}
                  className={`min-w-[32px] rounded-lg px-2 py-1.5 text-xs font-semibold ${
                    n === page
                      ? 'bg-indigo-600 text-white'
                      : 'border border-slate-200 bg-white text-slate-700 hover:bg-slate-50'
                  }`}
                >
                  {n}
                </button>
              ))}
              <button
                type="button"
                disabled={page >= totalPages || loading}
                onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                className="inline-flex items-center gap-1 rounded-lg border border-slate-200 bg-white px-2.5 py-1.5 text-xs font-semibold text-slate-700 hover:bg-slate-50 disabled:cursor-not-allowed disabled:opacity-40"
              >
                Next
                <ChevronRight size={14} />
              </button>
            </div>
          </div>
        ) : null}
      </div>

      {detailId ? (
        <div className="fixed inset-0 z-50 flex justify-end">
          <button
            type="button"
            className="absolute inset-0 bg-slate-900/40 backdrop-blur-[1px]"
            aria-label="Close detail"
            onClick={requestCloseDetail}
          />
          <div className="relative z-10 flex h-full w-full max-w-xl flex-col border-l border-slate-200 bg-white shadow-2xl">
            <div className="flex items-start justify-between gap-3 border-b border-slate-100 px-5 py-4">
              <div className="min-w-0">
                <p className="text-[11px] font-semibold uppercase tracking-wide text-indigo-600">
                  Partner detail
                </p>
                <h3 className="mt-0.5 truncate text-lg font-semibold text-slate-900">
                  {detailForm?.mechanic_name || detail?.mechanic_name || 'Loading…'}
                </h3>
                <p className="mt-0.5 font-mono text-xs text-slate-500">{detail?.code || detailId}</p>
              </div>
              <div className="flex shrink-0 items-center gap-1">
                {detail && detailForm ? (
                  <button
                    type="button"
                    disabled={detailSaving || !detailDirty}
                    onClick={saveDetailProfile}
                    className="inline-flex items-center gap-1 rounded-lg bg-indigo-600 px-2.5 py-1.5 text-xs font-semibold text-white hover:bg-indigo-700 disabled:cursor-not-allowed disabled:opacity-40"
                  >
                    {detailSaving ? <Loader2 size={13} className="animate-spin" /> : <Check size={13} />}
                    Save
                  </button>
                ) : null}
                {detail ? (
                  <button
                    type="button"
                    onClick={() => {
                      if (detailDirty && !window.confirm('Discard unsaved changes?')) return;
                      openCredits(detail);
                      closeDetail();
                    }}
                    className="inline-flex items-center gap-1 rounded-lg px-2.5 py-1.5 text-xs font-semibold text-emerald-700 hover:bg-emerald-50"
                  >
                    <Wallet size={13} />
                    Credits
                  </button>
                ) : null}
                <button
                  type="button"
                  onClick={requestCloseDetail}
                  className="rounded-lg p-1.5 text-slate-400 hover:bg-slate-100 hover:text-slate-700"
                >
                  <X size={18} />
                </button>
              </div>
            </div>

            <div className="flex-1 overflow-y-auto px-5 py-4">
              {detailLoading ? (
                <div className="flex items-center justify-center gap-2 py-16 text-sm text-slate-500">
                  <Loader2 className="animate-spin" size={18} />
                  Loading full profile…
                </div>
              ) : !detail || !detailForm ? (
                <div className="py-16 text-center text-sm text-slate-500">Could not load partner</div>
              ) : (
                <div className="space-y-5">
                  <div className="grid grid-cols-2 gap-2 sm:grid-cols-4">
                    <div className="rounded-xl border border-emerald-100 bg-emerald-50/60 px-3 py-2.5">
                      <p className="text-[10px] font-semibold uppercase text-emerald-700/80">Previous</p>
                      <input
                        type="number"
                        value={detailForm.previous_assigned_cases}
                        onChange={(e) => setDetailField('previous_assigned_cases', e.target.value)}
                        className="mt-0.5 w-full bg-transparent p-0 text-xl font-bold tabular-nums text-emerald-900 outline-none"
                      />
                    </div>
                    <div className="rounded-xl border border-indigo-100 bg-indigo-50/60 px-3 py-2.5">
                      <p className="text-[10px] font-semibold uppercase text-indigo-700/80">Team</p>
                      <p className="mt-0.5 text-xl font-bold tabular-nums text-indigo-900">
                        {detailCrew.length}
                      </p>
                    </div>
                    <button
                      type="button"
                      onClick={() =>
                        openTicketHistory({
                          id: detailId,
                          mechanic_name: detailForm?.mechanic_name || detail?.mechanic_name,
                          code: detail?.code,
                        })
                      }
                      className="rounded-xl border border-amber-100 bg-amber-50/60 px-3 py-2.5 text-left hover:bg-amber-50"
                      title="View ticket history"
                    >
                      <p className="text-[10px] font-semibold uppercase text-amber-800/80">Tickets</p>
                      <p className="mt-0.5 text-xl font-bold tabular-nums text-amber-950">
                        {detailTickets.available}
                      </p>
                      {detailTickets.reserved > 0 ? (
                        <p className="text-[10px] font-medium text-amber-800/70">
                          {detailTickets.reserved} reserved
                        </p>
                      ) : (
                        <p className="text-[10px] font-medium text-amber-800/70">View history</p>
                      )}
                    </button>
                    <div className="rounded-xl border border-indigo-100 bg-white px-3 py-2.5 shadow-sm">
                      <p className="text-[10px] font-semibold uppercase text-slate-500">Status</p>
                      <select
                        value={detailForm.active ? 'true' : 'false'}
                        onChange={(e) => setDetailField('active', e.target.value === 'true')}
                        className="mt-0.5 w-full cursor-pointer bg-transparent p-0 text-sm font-bold text-slate-900 outline-none"
                      >
                        <option value="true">Active</option>
                        <option value="false">Inactive</option>
                      </select>
                    </div>
                  </div>

                  <section>
                    <h4 className="mb-2 text-xs font-semibold uppercase tracking-wide text-slate-500">
                      Identity & contact
                    </h4>
                    <div className="grid gap-2 sm:grid-cols-2">
                      <EditableField label="ID" value={detail.id} mono disabled />
                      <EditableField
                        label="Code"
                        value={detailForm.code}
                        onChange={(v) => setDetailField('code', v)}
                        mono
                      />
                      <EditableField
                        label="Name"
                        value={detailForm.mechanic_name}
                        onChange={(v) => setDetailField('mechanic_name', v)}
                      />
                      <EditableField
                        label="Shop name"
                        value={detailForm.shop_name}
                        onChange={(v) => setDetailField('shop_name', v)}
                      />
                      <EditableField
                        label="Phone"
                        value={detailForm.number}
                        onChange={(v) => setDetailField('number', v)}
                      />
                      <EditableField
                        label="Alt phone 1"
                        value={detailForm.alternate_number1}
                        onChange={(v) => setDetailField('alternate_number1', v)}
                      />
                      <EditableField
                        label="Alt phone 2"
                        value={detailForm.alternate_number2}
                        onChange={(v) => setDetailField('alternate_number2', v)}
                      />
                      <EditableField
                        label="Timing"
                        value={detailForm.timing}
                        onChange={(v) => setDetailField('timing', v)}
                      />
                      <EditableField
                        label="Partner type"
                        value={detailForm.partner_type}
                        onChange={(v) => setDetailField('partner_type', v)}
                        options={[
                          { value: 'mechanic', label: 'Mechanic' },
                          { value: 'towing', label: 'Towing' },
                          { value: 'both', label: 'Mechanic + Towing' },
                        ]}
                      />
                      <EditableField
                        label="State"
                        value={detailForm.state}
                        onChange={(v) => setDetailField('state', v)}
                      />
                      <EditableField
                        label="District"
                        value={detailForm.district}
                        onChange={(v) => setDetailField('district', v)}
                      />
                      <EditableField
                        label="City"
                        value={detailForm.city}
                        onChange={(v) => setDetailField('city', v)}
                      />
                      <EditableField
                        label="Area"
                        value={detailForm.area}
                        onChange={(v) => setDetailField('area', v)}
                      />
                    </div>
                  </section>

                  <section>
                    <h4 className="mb-2 text-xs font-semibold uppercase tracking-wide text-slate-500">
                      Services & tags
                    </h4>
                    <div className="grid gap-2 sm:grid-cols-2">
                      <EditableField
                        label="Service tag"
                        value={detailForm.service_tag}
                        onChange={(v) => setDetailField('service_tag', v)}
                      />
                      <EditableField
                        label="Service tag 2"
                        value={detailForm.service_tag2}
                        onChange={(v) => setDetailField('service_tag2', v)}
                      />
                      <EditableField
                        label="Service tag 3"
                        value={detailForm.service_tag3}
                        onChange={(v) => setDetailField('service_tag3', v)}
                      />
                      <EditableField
                        label="Experience years"
                        type="number"
                        value={detailForm.experience_years}
                        onChange={(v) => setDetailField('experience_years', v)}
                      />
                      <EditableField
                        label="Service radius km"
                        type="number"
                        value={detailForm.service_radius_km}
                        onChange={(v) => setDetailField('service_radius_km', v)}
                      />
                      <EditableField
                        label="Center lat"
                        type="number"
                        value={detailForm.service_center_lat}
                        onChange={(v) => setDetailField('service_center_lat', v)}
                      />
                      <EditableField
                        label="Center lng"
                        type="number"
                        value={detailForm.service_center_lng}
                        onChange={(v) => setDetailField('service_center_lng', v)}
                      />
                    </div>
                  </section>

                  <section>
                    <h4 className="mb-2 text-xs font-semibold uppercase tracking-wide text-slate-500">
                      Service areas
                    </h4>
                    {Array.isArray(detail.service_areas) && detail.service_areas.length > 0 ? (
                      <div className="space-y-2">
                        {detail.service_areas.map((area, idx) => (
                          <div
                            key={idx}
                            className="rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm text-slate-700"
                          >
                            <span className="font-semibold text-slate-900">
                              {area.area || area.name || `Area ${idx + 1}`}
                            </span>
                            {[area.pincode, area.city, area.state, area.region]
                              .filter(Boolean)
                              .length ? (
                              <span className="text-slate-500">
                                {' '}
                                · {[area.pincode, area.city, area.state, area.region]
                                  .filter(Boolean)
                                  .join(' · ')}
                              </span>
                            ) : null}
                          </div>
                        ))}
                      </div>
                    ) : (
                      <p className="text-sm text-slate-400">No service areas</p>
                    )}
                  </section>

                  <section>
                    <h4 className="mb-2 text-xs font-semibold uppercase tracking-wide text-slate-500">
                      Login / public / KYC
                    </h4>
                    <div className="grid gap-2 sm:grid-cols-2">
                      <EditableField
                        label="Owner user id"
                        value={detailForm.owner_user_id}
                        onChange={(v) => setDetailField('owner_user_id', v)}
                        mono
                      />
                      <EditableField
                        label="Owner name"
                        value={detailForm.owner_name}
                        onChange={(v) => setDetailField('owner_name', v)}
                      />
                      <EditableField
                        label="Owner email"
                        value={detailForm.owner_email}
                        onChange={(v) => setDetailField('owner_email', v)}
                      />
                      <EditableField
                        label="Owner phone"
                        value={detailForm.owner_phone}
                        onChange={(v) => setDetailField('owner_phone', v)}
                      />
                      <EditableField
                        label="Owner status"
                        value={detailForm.owner_status}
                        onChange={(v) => setDetailField('owner_status', v)}
                        options={[
                          { value: '', label: '—' },
                          { value: 'active', label: 'Active' },
                          { value: 'inactive', label: 'Inactive' },
                          { value: 'suspended', label: 'Suspended' },
                        ].concat(
                          detailForm.owner_status &&
                            !['active', 'inactive', 'suspended'].includes(detailForm.owner_status)
                            ? [{ value: detailForm.owner_status, label: detailForm.owner_status }]
                            : []
                        )}
                      />
                      <EditableField
                        label="Public slug"
                        value={detailForm.public_slug}
                        onChange={(v) => setDetailField('public_slug', v)}
                        mono
                        placeholder="maharashtra-mumbai-andheri-shop-name"
                      />
                      <EditableField
                        label="Is public"
                        type="checkbox"
                        value={detailForm.is_public}
                        onChange={(v) => setDetailField('is_public', v)}
                      />
                      <EditableField
                        label="KYC status"
                        value={detailForm.kyc_status}
                        onChange={(v) => setDetailField('kyc_status', v)}
                        options={[
                          { value: '', label: '—' },
                          { value: 'pending', label: 'Pending' },
                          { value: 'verified', label: 'Verified' },
                          { value: 'rejected', label: 'Rejected' },
                        ].concat(
                          detailForm.kyc_status &&
                            !['', 'pending', 'verified', 'rejected'].includes(detailForm.kyc_status)
                            ? [{ value: detailForm.kyc_status, label: detailForm.kyc_status }]
                            : []
                        )}
                      />
                      <EditableField
                        label="KYC verified at"
                        value={detailForm.kyc_verified_at}
                        onChange={(v) => setDetailField('kyc_verified_at', v)}
                        placeholder="ISO timestamp"
                      />
                      <EditableField
                        label="KYC rejection"
                        value={detailForm.kyc_rejection_reason}
                        onChange={(v) => setDetailField('kyc_rejection_reason', v)}
                      />
                      <EditableField
                        label="Logo URL"
                        value={detailForm.logo_url}
                        onChange={(v) => setDetailField('logo_url', v)}
                        mono
                      />
                      <EditableField
                        label="Bio"
                        type="textarea"
                        value={detailForm.bio}
                        onChange={(v) => setDetailField('bio', v)}
                      />
                    </div>
                  </section>

                  <section>
                    <h4 className="mb-2 text-xs font-semibold uppercase tracking-wide text-slate-500">
                      Team ({detailCrew.length})
                    </h4>
                    {detailCrew.length === 0 ? (
                      <p className="text-sm text-slate-400">No team members yet</p>
                    ) : (
                      <div className="space-y-2">
                        {detailCrew.map((member) => (
                          <div
                            key={member.id}
                            className="rounded-lg border border-slate-200 bg-white px-3 py-2.5"
                          >
                            <div className="flex items-start justify-between gap-2">
                              <div>
                                <p className="text-sm font-semibold text-slate-900">
                                  {member.display_name || 'Crew'}
                                </p>
                                <p className="text-xs tabular-nums text-slate-500">
                                  {member.phone || '—'}
                                </p>
                              </div>
                              <span className="rounded bg-slate-100 px-1.5 py-0.5 text-[10px] font-semibold text-slate-600">
                                {member.status || 'active'}
                              </span>
                            </div>
                            <div className="mt-1.5 flex flex-wrap gap-1">
                              {member.can_negotiate_rates ? (
                                <span className="rounded bg-sky-50 px-1.5 py-0.5 text-[10px] font-semibold text-sky-700">
                                  Negotiate
                                </span>
                              ) : null}
                              {member.can_collect_payment ? (
                                <span className="rounded bg-amber-50 px-1.5 py-0.5 text-[10px] font-semibold text-amber-700">
                                  Collect
                                </span>
                              ) : null}
                              <span className="font-mono text-[10px] text-slate-400">
                                user: {member.user_id || '—'}
                              </span>
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                  </section>

                  <section>
                    <h4 className="mb-2 text-xs font-semibold uppercase tracking-wide text-slate-500">
                      Presence & meta
                    </h4>
                    <div className="grid gap-2 sm:grid-cols-2">
                      <EditableField label="Last presence" value={detail.last_presence_at} disabled />
                      <EditableField label="Presence source" value={detail.presence_source} disabled />
                      <EditableField
                        label="Auto online by slots"
                        type="checkbox"
                        value={detailForm.auto_online_by_slots}
                        onChange={(v) => setDetailField('auto_online_by_slots', v)}
                      />
                      <EditableField
                        label="City slug"
                        value={detailForm.city_slug}
                        onChange={(v) => setDetailField('city_slug', v)}
                        mono
                      />
                      <EditableField
                        label="Area slug"
                        value={detailForm.area_slug}
                        onChange={(v) => setDetailField('area_slug', v)}
                        mono
                      />
                      <EditableField label="Created at" value={detail.created_at} disabled />
                      <EditableField label="Updated at" value={detail.updated_at} disabled />
                      <EditableField label="Created by" value={detail.created_by} mono disabled />
                      <EditableField label="Updated by" value={detail.updated_by} mono disabled />
                    </div>
                  </section>

                  <section>
                    <h4 className="mb-2 text-xs font-semibold uppercase tracking-wide text-slate-500">
                      All DB fields (raw)
                    </h4>
                    <pre className="max-h-72 overflow-auto rounded-xl border border-slate-200 bg-slate-950 p-3 text-[11px] leading-relaxed text-slate-100">
                      {JSON.stringify(detail, null, 2)}
                    </pre>
                  </section>
                </div>
              )}
            </div>
            {detail && detailForm ? (
              <div className="flex items-center justify-between gap-3 border-t border-slate-100 bg-white px-5 py-3">
                <p className="text-xs text-slate-500">
                  {detailDirty ? 'Unsaved changes' : 'Click a field to edit, then Save'}
                </p>
                <div className="flex items-center gap-2">
                  <button
                    type="button"
                    disabled={!detailDirty || detailSaving}
                    onClick={resetDetailForm}
                    className="rounded-lg border border-slate-200 px-3 py-2 text-xs font-semibold text-slate-700 hover:bg-slate-50 disabled:cursor-not-allowed disabled:opacity-40"
                  >
                    Reset
                  </button>
                  <button
                    type="button"
                    disabled={!detailDirty || detailSaving}
                    onClick={saveDetailProfile}
                    className="inline-flex items-center gap-1 rounded-lg bg-indigo-600 px-3 py-2 text-xs font-semibold text-white hover:bg-indigo-700 disabled:cursor-not-allowed disabled:opacity-40"
                  >
                    {detailSaving ? <Loader2 size={13} className="animate-spin" /> : <Check size={13} />}
                    Save changes
                  </button>
                </div>
              </div>
            ) : null}
          </div>
        </div>
      ) : null}

      {editingId ? (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
          <button
            type="button"
            className="absolute inset-0 bg-slate-900/40 backdrop-blur-[1px]"
            aria-label="Close"
            onClick={closeEdit}
          />
          <div className="relative z-10 w-full max-w-lg overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-xl">
            <div className="flex items-start justify-between gap-3 border-b border-slate-100 px-5 py-4">
              <div className="min-w-0">
                <h3 className="text-lg font-semibold text-slate-900">Edit partner</h3>
                <p className="mt-0.5 truncate text-sm text-slate-500">{editName}</p>
              </div>
              <button
                type="button"
                onClick={closeEdit}
                className="rounded-lg p-1.5 text-slate-400 hover:bg-slate-100 hover:text-slate-700"
              >
                <X size={18} />
              </button>
            </div>

            <div className="space-y-4 px-5 py-4">
              <label className="block text-sm font-medium text-slate-700">
                Partner type
                <select
                  value={editType}
                  onChange={(e) => setEditType(e.target.value)}
                  className="mt-1.5 w-full rounded-lg border border-slate-200 px-3 py-2.5 text-sm focus:border-indigo-300 focus:outline-none focus:ring-2 focus:ring-indigo-500/20"
                >
                  <option value="mechanic">Mechanic</option>
                  <option value="towing">Towing</option>
                  <option value="both">Mechanic + Towing</option>
                </select>
              </label>

              <label className="block text-sm font-medium text-slate-700">
                RoadServe commission %
                <input
                  type="number"
                  min="0"
                  max="100"
                  value={editPercent}
                  onChange={(e) => setEditPercent(e.target.value)}
                  className="mt-1.5 w-full rounded-lg border border-slate-200 px-3 py-2.5 text-sm focus:border-indigo-300 focus:outline-none focus:ring-2 focus:ring-indigo-500/20"
                />
              </label>

              <label className="block text-sm font-medium text-slate-700">
                Owner user id (Partner app login)
                <input
                  type="text"
                  value={editOwner}
                  onChange={(e) => setEditOwner(e.target.value)}
                  placeholder="Optional if phone OTP already linked"
                  className="mt-1.5 w-full rounded-lg border border-slate-200 px-3 py-2.5 font-mono text-xs focus:border-indigo-300 focus:outline-none focus:ring-2 focus:ring-indigo-500/20"
                />
                <span className="mt-1 block text-xs font-normal text-slate-500">
                  Phone OTP usually auto-links. Manual paste only if needed.
                </span>
              </label>
            </div>

            <div className="flex justify-end gap-2 border-t border-slate-100 bg-slate-50/80 px-5 py-3">
              <button
                type="button"
                onClick={closeEdit}
                disabled={saving}
                className="rounded-lg border border-slate-200 bg-white px-4 py-2 text-sm font-semibold text-slate-700 hover:bg-slate-50 disabled:opacity-50"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={savePartnerSettings}
                disabled={saving}
                className="inline-flex items-center gap-2 rounded-lg bg-indigo-600 px-4 py-2 text-sm font-semibold text-white hover:bg-indigo-700 disabled:opacity-50"
              >
                {saving ? <Loader2 size={16} className="animate-spin" /> : null}
                {saving ? 'Saving…' : 'Save changes'}
              </button>
            </div>
          </div>
        </div>
      ) : null}

      {ticketHistoryPartner ? (
        <div className="fixed inset-0 z-[60] flex items-center justify-center p-4">
          <button
            type="button"
            className="absolute inset-0 bg-slate-900/40 backdrop-blur-[1px]"
            aria-label="Close ticket history"
            onClick={closeTicketHistory}
          />
          <div className="relative z-10 flex max-h-[88vh] w-full max-w-lg flex-col overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-xl">
            <div className="flex items-start justify-between gap-3 border-b border-slate-100 px-5 py-4">
              <div className="min-w-0">
                <h3 className="text-lg font-semibold text-slate-900">Ticket history</h3>
                <p className="mt-0.5 truncate text-sm text-slate-500">
                  {ticketHistoryPartner.mechanic_name ||
                    ticketHistoryPartner.code ||
                    ticketHistoryPartner.id}
                </p>
              </div>
              <button
                type="button"
                onClick={closeTicketHistory}
                className="rounded-lg p-1.5 text-slate-400 hover:bg-slate-100 hover:text-slate-700"
              >
                <X size={18} />
              </button>
            </div>

            <div className="border-b border-slate-100 px-5 py-3">
              <div className="grid grid-cols-2 gap-2">
                <div className="rounded-xl border border-amber-100 bg-amber-50/60 px-3 py-2.5">
                  <p className="text-[10px] font-semibold uppercase text-amber-800/80">Available</p>
                  <p className="mt-0.5 text-xl font-bold tabular-nums text-amber-950">
                    {Number(
                      ticketHistoryBalance?.total_available ??
                        ticketsByPartner[ticketHistoryPartner.id]?.available ??
                        0,
                    )}
                  </p>
                </div>
                <div className="rounded-xl border border-slate-200 bg-slate-50 px-3 py-2.5">
                  <p className="text-[10px] font-semibold uppercase text-slate-500">Reserved</p>
                  <p className="mt-0.5 text-xl font-bold tabular-nums text-slate-900">
                    {Number(
                      ticketHistoryBalance?.total_reserved ??
                        ticketsByPartner[ticketHistoryPartner.id]?.reserved ??
                        0,
                    )}
                  </p>
                </div>
              </div>
              {Array.isArray(ticketHistoryBalance?.products) &&
              ticketHistoryBalance.products.some((p) => Number(p.available || 0) > 0) ? (
                <div className="mt-2 flex flex-wrap gap-1.5">
                  {ticketHistoryBalance.products
                    .filter((p) => Number(p.available || 0) > 0)
                    .map((p) => (
                      <span
                        key={p.product_id}
                        className="rounded-md bg-amber-50 px-2 py-0.5 text-[11px] font-semibold text-amber-900 ring-1 ring-inset ring-amber-600/15"
                      >
                        {p.name}: {p.available}
                      </span>
                    ))}
                </div>
              ) : null}
            </div>

            <div className="flex gap-1 border-b border-slate-100 px-5 pt-2">
              <button
                type="button"
                onClick={() => setTicketHistoryTab('purchases')}
                className={`rounded-t-lg px-3 py-2 text-xs font-semibold ${
                  ticketHistoryTab === 'purchases'
                    ? 'bg-white text-indigo-700 ring-1 ring-inset ring-slate-200'
                    : 'text-slate-500 hover:text-slate-800'
                }`}
              >
                Purchases ({ticketHistoryPurchases.length})
              </button>
              <button
                type="button"
                onClick={() => setTicketHistoryTab('usage')}
                className={`rounded-t-lg px-3 py-2 text-xs font-semibold ${
                  ticketHistoryTab === 'usage'
                    ? 'bg-white text-indigo-700 ring-1 ring-inset ring-slate-200'
                    : 'text-slate-500 hover:text-slate-800'
                }`}
              >
                Used on jobs ({ticketHistoryHolds.length})
              </button>
            </div>

            <div className="min-h-0 flex-1 overflow-y-auto px-5 py-3">
              {ticketHistoryLoading ? (
                <div className="flex items-center justify-center gap-2 py-10 text-sm text-slate-500">
                  <Loader2 className="animate-spin" size={16} />
                  Loading history…
                </div>
              ) : ticketHistoryTab === 'purchases' ? (
                ticketHistoryPurchases.length === 0 ? (
                  <p className="py-8 text-center text-sm text-slate-400">No ticket purchases yet</p>
                ) : (
                  <div className="space-y-1.5">
                    {ticketHistoryPurchases.map((row) => (
                      <div
                        key={row.id}
                        className="flex items-center justify-between gap-2 rounded-lg border border-slate-100 bg-slate-50/70 px-3 py-2.5"
                      >
                        <div className="min-w-0">
                          <p className="truncate text-sm font-semibold text-slate-900">
                            {row.product_name}
                          </p>
                          <p className="text-xs text-slate-500">
                            {formatWhen(row.paid_at || row.created_at)}
                            {row.tickets_credited
                              ? ` · +${row.tickets_credited} ticket${
                                  Number(row.tickets_credited) === 1 ? '' : 's'
                                }`
                              : ''}
                          </p>
                        </div>
                        <div className="shrink-0 text-right">
                          <p className="text-sm font-bold tabular-nums text-slate-900">
                            ₹{Number(row.amount_inr || 0).toLocaleString('en-IN')}
                          </p>
                          <span
                            className={`inline-flex rounded-full px-2 py-0.5 text-[10px] font-semibold uppercase ${
                              row.status === 'paid'
                                ? 'bg-emerald-50 text-emerald-700'
                                : row.status === 'failed'
                                  ? 'bg-red-50 text-red-700'
                                  : 'bg-amber-50 text-amber-700'
                            }`}
                          >
                            {row.status || 'pending'}
                          </span>
                        </div>
                      </div>
                    ))}
                  </div>
                )
              ) : ticketHistoryHolds.length === 0 ? (
                <p className="py-8 text-center text-sm text-slate-400">No tickets used on jobs yet</p>
              ) : (
                <div className="space-y-1.5">
                  {ticketHistoryHolds.map((row) => {
                    const st = HOLD_STATUS[row.status] || HOLD_STATUS.released;
                    const jobLabel =
                      row.request?.service_type ||
                      (row.service_request_id
                        ? `Job ${String(row.service_request_id).slice(0, 8)}`
                        : 'Job');
                    return (
                      <div
                        key={row.id}
                        className="flex items-center justify-between gap-2 rounded-lg border border-slate-100 bg-slate-50/70 px-3 py-2.5"
                      >
                        <div className="min-w-0">
                          <p className="truncate text-sm font-semibold text-slate-900">{jobLabel}</p>
                          <p className="truncate text-xs text-slate-500">
                            {row.product_name}
                            {' · '}
                            {formatWhen(row.updated_at || row.created_at)}
                          </p>
                        </div>
                        <span
                          className={`shrink-0 rounded-full px-2 py-0.5 text-[10px] font-semibold uppercase ${st.className}`}
                        >
                          {st.label}
                        </span>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          </div>
        </div>
      ) : null}

      {creditPartner ? (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
          <button
            type="button"
            className="absolute inset-0 bg-slate-900/40 backdrop-blur-[1px]"
            aria-label="Close"
            onClick={closeCredits}
          />
          <div className="relative z-10 w-full max-w-lg overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-xl">
            <div className="flex items-start justify-between gap-3 border-b border-slate-100 px-5 py-4">
              <div className="min-w-0">
                <h3 className="text-lg font-semibold text-slate-900">Wallet credits</h3>
                <p className="mt-0.5 truncate text-sm text-slate-500">
                  {creditPartner.mechanic_name || creditPartner.code || creditPartner.id}
                </p>
              </div>
              <button
                type="button"
                onClick={closeCredits}
                className="rounded-lg p-1.5 text-slate-400 hover:bg-slate-100 hover:text-slate-700"
              >
                <X size={18} />
              </button>
            </div>

            <div className="space-y-4 px-5 py-4">
              <div className="grid grid-cols-2 gap-2">
                <div className="rounded-xl border border-emerald-100 bg-emerald-50/60 px-3 py-2.5">
                  <p className="text-[10px] font-semibold uppercase text-emerald-700/80">
                    Wallet balance
                  </p>
                  <p className="mt-0.5 text-xl font-bold tabular-nums text-emerald-900">
                    ₹{creditAvailable.toLocaleString('en-IN')}
                  </p>
                </div>
                <div className="rounded-xl border border-amber-100 bg-amber-50/60 px-3 py-2.5">
                  <p className="text-[10px] font-semibold uppercase text-amber-700/80">
                    To repay
                  </p>
                  <p className="mt-0.5 text-xl font-bold tabular-nums text-amber-900">
                    ₹{creditOutstanding.toLocaleString('en-IN')}
                  </p>
                </div>
              </div>

              <label className="block text-sm font-medium text-slate-700">
                Credit amount (₹)
                <input
                  type="number"
                  min="1"
                  value={creditAmount}
                  onChange={(e) => setCreditAmount(e.target.value)}
                  placeholder="e.g. 500"
                  className="mt-1.5 w-full rounded-lg border border-slate-200 px-3 py-2.5 text-sm focus:border-indigo-300 focus:outline-none focus:ring-2 focus:ring-indigo-500/20"
                />
              </label>

              <label className="flex items-start gap-2.5 rounded-lg border border-slate-200 bg-slate-50/70 px-3 py-2.5">
                <input
                  type="checkbox"
                  checked={creditRepayable}
                  onChange={(e) => setCreditRepayable(e.target.checked)}
                  className="mt-0.5 h-4 w-4 rounded border-slate-300 text-indigo-600 focus:ring-indigo-500"
                />
                <span className="text-sm text-slate-700">
                  <span className="font-semibold text-slate-900">Repayable advance</span>
                  <span className="mt-0.5 block text-xs text-slate-500">
                    Partner sees this amount as “to repay” in the app wallet and can repay from
                    wallet.
                  </span>
                </span>
              </label>

              <label className="block text-sm font-medium text-slate-700">
                Note (shown in ledger)
                <input
                  type="text"
                  value={creditNote}
                  onChange={(e) => setCreditNote(e.target.value)}
                  placeholder="Optional reason"
                  className="mt-1.5 w-full rounded-lg border border-slate-200 px-3 py-2.5 text-sm focus:border-indigo-300 focus:outline-none focus:ring-2 focus:ring-indigo-500/20"
                />
              </label>

              <div>
                <h4 className="mb-2 text-xs font-semibold uppercase tracking-wide text-slate-500">
                  Recent grants
                </h4>
                {creditLoading ? (
                  <div className="flex items-center gap-2 py-4 text-sm text-slate-500">
                    <Loader2 className="animate-spin" size={16} />
                    Loading…
                  </div>
                ) : creditRows.length === 0 ? (
                  <p className="text-sm text-slate-400">No credits given yet</p>
                ) : (
                  <div className="max-h-48 space-y-1.5 overflow-y-auto">
                    {creditRows.map((row) => (
                      <div
                        key={row.id}
                        className="flex items-center justify-between gap-2 rounded-lg border border-slate-100 bg-slate-50/70 px-3 py-2"
                      >
                        <div className="min-w-0">
                          <p className="text-sm font-semibold text-slate-900">
                            ₹{Number(row.amount || 0).toLocaleString('en-IN')}
                            {row.repayable ? (
                              <span className="ml-2 rounded bg-amber-100 px-1.5 py-0.5 text-[10px] font-semibold uppercase text-amber-800">
                                Advance
                              </span>
                            ) : null}
                          </p>
                          <p className="truncate text-xs text-slate-500">
                            {new Date(row.created_at).toLocaleDateString('en-IN')}
                            {row.note ? ` · ${row.note}` : ''}
                          </p>
                        </div>
                        {row.repayable ? (
                          <span className="shrink-0 text-xs font-semibold text-amber-700">
                            ₹{Number(row.outstanding || 0).toLocaleString('en-IN')} due
                          </span>
                        ) : null}
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>

            <div className="flex justify-end gap-2 border-t border-slate-100 bg-slate-50/80 px-5 py-3">
              <button
                type="button"
                onClick={closeCredits}
                disabled={creditSaving}
                className="rounded-lg border border-slate-200 bg-white px-4 py-2 text-sm font-semibold text-slate-700 hover:bg-slate-50 disabled:opacity-50"
              >
                Close
              </button>
              <button
                type="button"
                onClick={grantCredits}
                disabled={creditSaving}
                className="inline-flex items-center gap-2 rounded-lg bg-emerald-600 px-4 py-2 text-sm font-semibold text-white hover:bg-emerald-700 disabled:opacity-50"
              >
                {creditSaving ? <Loader2 size={16} className="animate-spin" /> : <Wallet size={16} />}
                {creditSaving ? 'Adding…' : creditRepayable ? 'Give advance' : 'Give credits'}
              </button>
            </div>
          </div>
        </div>
      ) : null}

      <PartnerCoverageMap open={coverageOpen} onClose={() => setCoverageOpen(false)} />

      {previewPartner?.mechanic_id ? (
        <MechanicAppPreviewModal
          mechanicId={previewPartner.mechanic_id}
          initialGroup={previewPartner}
          onClose={() => setPreviewPartner(null)}
          onChanged={() => {
            loadMechanics();
            loadStats();
          }}
        />
      ) : null}
    </div>
  );
};

export default MechanicManagement;
